import styled from "@emotion/styled";
import { FC, ReactNode, useId } from "react";
import {
  Dialog,
  Heading,
  Modal,
  ModalOverlay,
  ModalOverlayProps
} from "react-aria-components";
import { PopupDialogCloseButton } from "./PopupDialogCloseButton";
import { IconDialogClose } from "../../../icons/IconDialogClose"; // Don't change this. `icons` folder is outside `src` -> results in a type check error without the dots

const StyledModalOverlay = styled(ModalOverlay)`
  label: PopupDialogOverlay;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: all;
  background-color: var(--color-charcoal-60);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  &[data-entering] {
    animation: overlay-fade-in 0.25s cubic-bezier(0.32, 0.72, 0, 1) forwards;
  }

  &[data-exiting] {
    animation: overlay-fade-out 0.2s cubic-bezier(0.32, 0.72, 0, 1) forwards;
  }

  @keyframes overlay-fade-in {
    from {
      background-color: transparent;
    }
    to {
      background-color: var(--color-charcoal-60);
    }
  }

  @keyframes overlay-fade-out {
    from {
      background-color: var(--color-charcoal-60);
    }
    to {
      background-color: transparent;
    }
  }
`;

const StyledModal = styled(Modal)`
  label: PopupDialogModal;
  --popup-dialog-width: min(680px, calc(100vw - 3.2rem));
  flex: 0 0 var(--popup-dialog-width);
  border-radius: 0.5rem;
  background: var(--Primary-Cream, #fffcf3);
  padding: 0;
  text-align: left;
  font-family: "STK Bureau", Sans-Serif, sans-serif;
  font-feature-settings:
    "liga" off,
    "clig" off;

  &[data-entering] {
    animation: modal-pop-in 0.35s cubic-bezier(0.32, 0.72, 0, 1) 0.05s both;
  }

  &[data-exiting] {
    animation: modal-pop-out 0.2s cubic-bezier(0.32, 0.72, 0, 1) forwards;
  }

  @keyframes modal-pop-in {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes modal-pop-out {
    from {
      opacity: 1;
      transform: scale(1);
    }
    to {
      opacity: 0;
      transform: scale(0.95);
    }
  }
`;

const StyledDialog = styled(Dialog)`
  position: relative;
  padding: 1.5rem 1.5rem 2rem;
  display: flex;
  flex-direction: column;
`;

const StyledHeading = styled(Heading)`
  color: var(--color-charcoal, #212121);
  font-family: "STK Bureau", Sans-Serif, sans-serif;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: -0.025em;
  padding-right: 2rem;
`;

const Content = styled.div`
  padding-top: 1rem;
  color: var(--color-charcoal, #212121);
  font-size: 1rem;
  line-height: 140%;
  letter-spacing: -0.02em;
`;

const Actions = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-end;
  align-items: center;
  padding-top: 1.5rem;
`;

interface PopupDialogProps extends ModalOverlayProps {
  title?: ReactNode;
  actions?: ReactNode | ((opts: { close: () => void }) => ReactNode);
  children?: ReactNode;
  onClose?: () => void;
  actionsGap?: string;
}

export const PopupDialog: FC<PopupDialogProps> = ({
  title,
  actions,
  children,
  onClose,
  actionsGap,
  isDismissable = true,
  ...props
}) => {
  const contentId = useId();

  return (
    <StyledModalOverlay isDismissable={isDismissable} {...props}>
      <StyledModal>
        <StyledDialog aria-describedby={children ? contentId : undefined}>
          {({ close }) => (
            <>
              <PopupDialogCloseButton
                slot={"close"}
                onPress={onClose}
                aria-label="Close"
              >
                <IconDialogClose aria-hidden={true} />
              </PopupDialogCloseButton>
              {title && <StyledHeading slot="title">{title}</StyledHeading>}
              {children && <Content id={contentId}>{children}</Content>}
              {actions && (
                <Actions style={{ gap: actionsGap ?? "0" }}>
                  {typeof actions === "function" ? actions({ close }) : actions}
                </Actions>
              )}
            </>
          )}
        </StyledDialog>
      </StyledModal>
    </StyledModalOverlay>
  );
};
